/**
 * Event names for communicating between components
 */
export const EVENTS = {
  PH_ANALYSIS_COMPLETED: "ph_analysis_completed",
  MEMBER_CHANGED: "member_changed"
};

/**
 * Register a custom event listener for pH analysis completion
 * @param callback Function to call when pH analysis is completed
 * @returns Cleanup function to remove the listener
 */
export function onPhAnalysisCompleted(callback: (groupMemberId?: number) => void) {
  const handler = (event: CustomEvent) => {
    callback(event.detail?.groupMemberId);
  };

  window.addEventListener(EVENTS.PH_ANALYSIS_COMPLETED, handler as EventListener);

  return () => {
    window.removeEventListener(EVENTS.PH_ANALYSIS_COMPLETED, handler as EventListener);
  };
}

/**
 * Register a custom event listener for member changes
 * @param callback Function to call when member changes
 * @returns Cleanup function to remove the listener
 */
export function onMemberChanged(callback: (groupMemberId?: number) => void) {
  const handler = (event: CustomEvent) => {
    callback(event.detail?.groupMemberId);
  };

  window.addEventListener(EVENTS.MEMBER_CHANGED, handler as EventListener);

  return () => {
    window.removeEventListener(EVENTS.MEMBER_CHANGED, handler as EventListener);
  };
}

/**
 * Dispatch pH analysis completed event
 * @param groupMemberId Optional group member ID
 */
export function dispatchPhAnalysisCompleted(groupMemberId?: number) {
  console.log("Dispatching pH analysis completed event", { groupMemberId });
  window.dispatchEvent(
    new CustomEvent(EVENTS.PH_ANALYSIS_COMPLETED, {
      detail: { groupMemberId }
    })
  );
}

/**
 * Dispatch member changed event
 * Used to notify components that a member has changed
 * @param groupMemberId Optional group member ID
 */
export function dispatchMemberChanged(groupMemberId?: number) {
  console.log("Dispatching member changed event", { groupMemberId });
  window.dispatchEvent(
    new CustomEvent(EVENTS.MEMBER_CHANGED, {
      detail: { groupMemberId }
    })
  );
}

import { QueryClient } from "@tanstack/react-query";

/**
 * Invalidate member recommendations in the query cache
 * @param queryClient The React Query client
 * @param groupMemberId Optional group member ID
 */
export function invalidateMemberRecommendations(queryClient: QueryClient, groupMemberId?: number) {
  console.log("Invalidating member recommendations cache", { groupMemberId });
  if (groupMemberId) {
    queryClient.invalidateQueries({
      queryKey: ["memberRecommendations", groupMemberId]
    });
  } else {
    queryClient.invalidateQueries({
      queryKey: ["memberRecommendations"]
    });
  }
}
